import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { DialogDescription } from "@radix-ui/react-dialog";
import { IconBook, IconCloudUpload } from "@tabler/icons-react";
import { useEffect, useState, useRef, useMemo } from "react";
import { LibraryMedia } from "@/types";

import {
  addLibraryMedia,
  getLibraryMedia,
  deleteLibraryMedia,
} from "@/api/globalApiService";
import { useParams } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { IconDotsVertical, IconDownload, IconTrash } from "@tabler/icons-react";
import { downloadMedia } from "@/components/common/adCard/actions/DownloadAction";
import { uploadFilesAsync } from "@/api/globalApiService";
import Lottie from "lottie-react";
import uploadingLoader from "@/assets/uploading_loader.json";
import { LuInfo } from "react-icons/lu";

import { toast } from "sonner";
import ToastContent from "@/components/common/toast/ToastContent";
import EmptySVG from "./empty.svg";

import { IconSortDescending, IconSortAscending } from "@tabler/icons-react";

type UploadingFile = {
  file: File;
  preview: string;
  id: string;
};

interface Props {
  attachments: LibraryMedia[];
  onAttachmentChange: (attachments: LibraryMedia[]) => void;
  attachLimit?: number;
  limitMessage?: string;
  // Optional custom trigger to open the dialog (e.g., a dropdown item)
  trigger?: React.ReactNode;
  // Optional callback when dialog open state changes
  onOpenChange?: (open: boolean) => void;
}
const MediaLibraryDialog = ({
  onAttachmentChange,
  attachLimit,
  limitMessage,
  attachments,
  trigger,
  onOpenChange: externalOnOpenChange,
}: Props) => {
  const [open, setOpen] = useState<boolean>(false);
  const [mediaList, setMediaList] = useState<LibraryMedia[]>([]);
  const [loadingMediaList, setLoadingMediaList] = useState<boolean>(false);
  const [uploadingItems, setUploadingItems] = useState<UploadingFile[]>([]);

  const [isUploading, setUploading] = useState<boolean>(false);
  // Holds the media objects currently selected in the dialog
  const [selectedMedia, setSelectedMedia] = useState<LibraryMedia[]>([]);
  // Only two sort orders: descending (default) and ascending
  const [sortDesc, setSortDesc] = useState<boolean>(true);

  // Toggle selection for a given media object
  const toggleSelect = (media: LibraryMedia) => {
    setSelectedMedia((prev) => {
      const exists = prev.some((m) => m.id === media.id);
      if (exists) {
        return prev.filter((m) => m.id !== media.id);
      }

      // Enforce attachment limit if provided
      if (attachLimit !== undefined && prev.length >= attachLimit) {
        if (limitMessage) {
          toast.message(
            <ToastContent
              icon={LuInfo}
              headingPrefix={"Attachment limit reached."}
              subheadingPrefix={limitMessage}
              isError={false}
              isWarning={true}
            />,
          );
        }
        return prev; // Do not add more selections
      }
      return [...prev, media];
    });
  };
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { accountId } = useParams();
  useEffect(() => {
    if (!accountId) return;
    setLoadingMediaList(true);
    getLibraryMedia(accountId)
      .then((res) => {
        setMediaList(res.data);
      })
      .catch((error) => {
        console.error(error);
        //// handle error
      })
      .finally(() => {
        setLoadingMediaList(false);
      });
  }, [accountId]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!accountId) return;
    const filesArray = Array.from(e.target.files || []);

    if (!filesArray.length) return;

    // Create immediate preview items with temp IDs
    const newUploadingItems = filesArray.map((file) => {
      const tempId = `temp-${Date.now()}-${Math.random()}`;
      const preview = URL.createObjectURL(file);
      return { file, preview, id: tempId };
    });

    setUploadingItems((prev) => [...newUploadingItems, ...prev]);
    setUploading(true);

    try {
      // 1. Upload raw files -> get back ids
      const res = await uploadFilesAsync(filesArray, accountId);
      const file_ids = res.files.map((f) => f.id);

      // 2. Add uploaded files to media library and obtain the final media
      const libMediaRes = await addLibraryMedia(file_ids, accountId);

      // 3. Add uploaded items to the list, replacing duplicates by file_id
      setMediaList((prev) => {
        const combined = [...libMediaRes.data, ...prev];
        const uniqueMap = new Map<string, LibraryMedia>();
        // Process in order to keep latest (first occurrence wins)
        combined.forEach((item) => {
          if (!uniqueMap.has(item.file_id)) {
            uniqueMap.set(item.file_id, item);
          }
        });
        return Array.from(uniqueMap.values());
      });

      // 4. Clean up blob URLs to prevent memory leaks
      newUploadingItems.forEach((item) => {
        if (item.preview.startsWith("blob:")) {
          URL.revokeObjectURL(item.preview);
        }
      });
    } catch (error) {
      console.error(error);

      // Clean up failed upload previews only
      newUploadingItems.forEach((item) => {
        if (item.preview.startsWith("blob:")) {
          URL.revokeObjectURL(item.preview);
        }
      });
    } finally {
      setUploading(false);
      setUploadingItems([]);
      // Reset file input
      if (e.target) {
        e.target.value = "";
      }
    }
  };

  const handleDeleteMedia = async (id: string) => {
    if (!accountId) return;
    try {
      await deleteLibraryMedia(id, accountId);
      setMediaList((prev) => prev.filter((m) => m.id !== id));
      // Remove from selection if it was selected
      setSelectedMedia((prev) => prev.filter((m) => m.id !== id));
    } catch (error) {
      console.error(error);
      // TODO: error handling (toast)
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    externalOnOpenChange?.(isOpen);
  };

  const handleAttach = () => {
    onAttachmentChange(selectedMedia);
    handleOpenChange(false);
  };
  // on open sync selection with attachments
  useEffect(() => {
    if (open) {
      setSelectedMedia(attachments);
    }
  }, [open, attachments]);

  // Derived sorted media list
  const sortedMediaList = useMemo(() => {
    const list = [...mediaList];
    return list.sort((a, b) =>
      sortDesc
        ? new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        : new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    );
  }, [mediaList, sortDesc]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button>
            <IconBook size={16} color="#9ba2bd" />
            <span className="font-normal text-[12px] text-brand-primary-800 relative top-[1px]">
              Library
            </span>
          </Button>
        )}
      </DialogTrigger>
      <DialogContent
        className="flex flex-col border w-fit max-w-[90%] h-[88%] focus:outline-none p-4"
        hideCloseButton
      >
        <DialogHeader className="flex flex-row w-full justify-between">
          <div className="flex flex-col gap-2">
            <DialogTitle className="flex gap-2">
              <IconBook size={16} color="#9ba2bd" />
              <span className="font-medium text-[13px] leading-[18px] tracking-[0.02em] text-brand-primary-800">
                Library
              </span>
            </DialogTitle>
            <DialogDescription className="font-normal not-italic text-[12px] leading-[18px] tracking-[0.02em] text-[#02185999]">
              Upload and manage files across the team.
            </DialogDescription>
          </div>
          <Button
            onClick={() => setSelectedMedia([])}
            disabled={selectedMedia.length === 0}
          >
            Clear All
          </Button>
        </DialogHeader>
        <div className="flex-1 border border-border-gray rounded-[12px] flex flex-col min-h-0">
          {uploadingItems.length === 0 && !loadingMediaList && mediaList.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center w-[786px]">
              <img src={EmptySVG} alt="Empty library" />
              <h6 className="font-medium text-[13px] leading-[100%] text-center text-brand-primary-800 relative -top-[32px]">
                Nothing here yet.
              </h6>
              <p className="text-brand-primary-400 font-normal text-[12px] leading-[100%] tracking-[0] text-center  relative -top-[26px]">
                Upload JPEG/PNG files to get started.
              </p>
            </div>
          ) : (
            <>
              <button
                type="button"
                className="flex items-center w-fit gap-1 text-[18px] font-medium text-[#5B6690] focus:outline-none bg-transparent border-0 px-3 py-2"
                onClick={() => setSortDesc((prev) => !prev)}
                style={{ boxShadow: "none" }}
              >
                <span className="font-normal not-italic text-[12px] leading-[20px] tracking-[0] text-brand-primary-800 relative top-[1px]">
                  Date Uploaded
                </span>
                {sortDesc ? (
                  <IconSortDescending size={16} color="#02185966" />
                ) : (
                  <IconSortAscending size={16} color="#02185966" />
                )}
              </button>

              <div className="flex-1 overflow-y-auto min-h-0 scrollbar-hide">
                <div className="max-w-full grid grid-cols-[repeat(6,120px)] gap-2 p-3 pt-0 justify-start content-start min-h-[180px]">
                  {uploadingItems.map((item) => (
                    <MediaCard
                      key={item.id}
                      media={null}
                      isUploading={true}
                      uploadingItem={item}
                      onDelete={() => {}}
                      isSelected={false}
                      onToggle={() => {}}
                    />
                  ))}
                  {sortedMediaList.map((item) => (
                    <MediaCard
                      key={item.id}
                      media={item}
                      isUploading={false}
                      uploadingItem={null}
                      onDelete={(id: string) => void handleDeleteMedia(id)}
                      isSelected={selectedMedia.some((m) => m.id === item.id)}
                      onToggle={() => toggleSelect(item)}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
        {/* Hidden file input for uploading images */}
        <input
          type="file"
          accept="image/jpeg,image/png"
          multiple
          ref={fileInputRef}
          onChange={(e) => void handleFileUpload(e)}
          className="hidden"
        />
        <DialogFooter className="flex sm:justify-between md:justify-between lg:justify-between">
          <Button onClick={() => fileInputRef.current?.click()} disabled={isUploading}>
            <IconCloudUpload color="#68749c" size={16} />
            <span className="font-normal text-[12px] leading-[19.16px] tracking-[0.02em] text-brand-primary-800">
              Upload Files
            </span>
          </Button>
          <div className="flex gap-2 items-center">
            {selectedMedia.length !== 0 && (
              <div className="flex items-center justify-center bg-[#FF4500] text-white rounded-full h-[24px] w-[24px] font-medium text-[14px] leading-[16.48px] tracking-[0]">
                <span className="relative top-[1.2px]">{selectedMedia.length}</span>
              </div>
            )}
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              className="border-0 bg-[#FF4500] text-white hover:bg-[#F42100] w-[64px]"
              onClick={handleAttach}
              disabled={attachments.length === 0 && selectedMedia.length === 0}
            >
              {selectedMedia.length === 0 ? "Apply" : "Attach"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Delete handler is passed from parent

interface MediaCardProps {
  media: LibraryMedia | null;
  onDelete: (id: string) => void;
  isSelected: boolean;
  onToggle: () => void;
  isUploading: boolean;
  uploadingItem?: UploadingFile | null;
}

const MediaCard = ({
  media,
  onDelete,
  isSelected,
  onToggle,
  isUploading,
  uploadingItem,
}: MediaCardProps) => {
  const handleCardClick = () => {
    if (!isUploading) {
      onToggle();
    }
  };

  // Determine image source
  const getImageSource = (): string | undefined => {
    if (isUploading) {
      return uploadingItem?.preview;
    }
    return media?.file_url;
  };
  const imageSrc = getImageSource();
  const altText = isUploading ? uploadingItem?.file.name : media?.file_name || "media";
  return (
    <div
      className="relative h-[120px] w-[120px] cursor-pointer group"
      onClick={handleCardClick}
      tabIndex={0}
      role="button"
      aria-pressed={isSelected}
    >
      {/* Selection checkbox - only show for uploaded items */}
      {!isUploading && (
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggle()}
          className="absolute top-2 left-2 bg-[#FFFFFF33] z-10 border-[1px] border-[#F0F0F0] shadow-sm"
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
        />
      )}

      {/* Actions dropdown - only show for uploaded items */}
      {!isUploading && media && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              onClick={(e) => e.stopPropagation()}
              className="absolute top-2 right-2 z-10 bg-[#00000066] hover:bg-[#00000099] backdrop-blur-sm p-1 rounded-[8px] opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100 pointer-events-none group-hover:pointer-events-auto data-[state=open]:pointer-events-auto transition-opacity w-[28px] h-[28px] flex items-center justify-center"
            >
              <IconDotsVertical size={14} color="#fff" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            side="bottom"
            className="rounded-[12px] border border-border-gray p-1"
            onClick={(e) => e.stopPropagation()}
          >
            <DropdownMenuItem
              className="flex items-center gap-2 cursor-pointer focus:bg-[#F6F6F6] hover:bg-[#F6F6F6] focus:text-inherit"
              onClick={() => void downloadMedia(media.file_url, media.file_name)}
            >
              <IconDownload size={14} color="#02185999" />
              <span className="text-[12px] text-brand-primary-800">Download</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="flex items-center gap-2 cursor-pointer focus:bg-[#F6F6F6] hover:bg-[#F6F6F6] focus:text-inherit"
              onClick={() => onDelete(media.id)}
            >
              <IconTrash size={14} color="#D92D20" />
              <span className="text-[12px] text-brand-primary-800">Delete</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Main image - show directly */}
      {imageSrc && (
        <img
          src={imageSrc}
          className="object-cover w-[120px] h-[120px] rounded-[8px]"
          style={{
            height: "120px",
            width: "120px",
          }}
          alt={altText}
          draggable={false}
        />
      )}

      {/* Upload overlay - only show while uploading */}
      {isUploading && (
        <div className="absolute inset-0 bg-white/40 grid place-items-center backdrop-blur-[4px] rounded-[8px]">
          <Lottie
            animationData={uploadingLoader}
            loop
            autoplay
            style={{ transform: "scale(2)", position: "relative", top: 5, left: 7 }}
          />
        </div>
      )}
    </div>
  );
};

export default MediaLibraryDialog;
